import { Component, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "../../../services/auth/auth.service";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { MetaDataService } from "../../../services/meta/meta-data.service";
import { ImageManager } from "../../../shared/image-manager";


@Component({
    selector: 'xplorer-bowser-reset-password-page',
    templateUrl: './reset-password-page.component.html',
    styleUrls: ['./reset-password-page.component.css'],
    imports: [ReactiveFormsModule, TranslateModule]
})
export class ResetPasswordPageComponent implements OnInit {
  resetPasswordForm: FormGroup;
  isSubmitting = false;
  isValidating = true;
  tokenValid = false;
  validationMessage: string;
  tokenErrorMessage: string;
  serverErrorMessage: string;
  emailFromToken: string;
  token: string | null = null;

  private fb: FormBuilder = inject(FormBuilder);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private authService: AuthService = inject(AuthService);
  private router: Router = inject(Router);
  private translateService: TranslateService = inject(TranslateService);
  private metaDataService: MetaDataService = inject(MetaDataService);


  ngOnInit(): void {
    this.metaDataService.setDefaultMetaData();
    this.buildForm();
    this.initializeToken();
  }

  private initializeToken() {
    this.token = this.route.snapshot.queryParamMap.get('token');
    if (!this.token) {
      this.isValidating = false;
      this.tokenValid = false;
      this.tokenErrorMessage = this.translateService.instant('reset_password.missing_token_error');
      return;
    }

    const token = this.token;

    this.authService.validateResetToken(token).subscribe({
      next: (response) => {
        this.isValidating = false;
        this.tokenValid = response.valid;
        this.validationMessage = response.message;
        this.emailFromToken = response.email;
        if (!response.valid) {
          this.tokenErrorMessage = response.message || this.translateService.instant('reset_password.invalid_token_error');
        }
      },
      error: (error) => {
        this.isValidating = false;
        this.tokenValid = false;
        this.tokenErrorMessage = error?.error?.message || this.translateService.instant('reset_password.invalid_token_error');
      }
    });
  }

  onSubmit() {
    if (this.resetPasswordForm.invalid || !this.tokenValid || this.isSubmitting) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.serverErrorMessage = undefined;

    const { password, confirmPassword } = this.resetPasswordForm.value;
    if (!this.token) {
      this.serverErrorMessage = this.translateService.instant('reset_password.missing_token_error');
      this.isSubmitting = false;
      return;
    }

    this.authService.resetPassword({ token: this.token, password, confirmPassword }).subscribe({
      next: () => {
        this.isSubmitting = false;
        const messageKey = 'reset_password.success_redirect_message';
        this.router.navigate(['login'], { state: { passwordFlowMessage: messageKey } });
      },
      error: (error) => {
        this.isSubmitting = false;
        this.serverErrorMessage = error?.error?.message || this.translateService.instant('reset_password.generic_error');
      }
    });
  }

  goToForgotPassword() {
    this.router.navigate(['forgot-password']);
  }

  goToLogin() {
    this.router.navigate(['login']);
  }

  get passwordControl(): AbstractControl {
    return this.resetPasswordForm.get('password');
  }

  get confirmPasswordControl(): AbstractControl {
    return this.resetPasswordForm.get('confirmPassword');
  }

  get shouldShowPasswordError(): boolean {
    return this.passwordControl?.invalid && (this.passwordControl?.dirty || this.passwordControl?.touched);
  }

  get shouldShowConfirmError(): boolean {
    return (this.confirmPasswordControl?.invalid && (this.confirmPasswordControl?.dirty || this.confirmPasswordControl?.touched))
      || (this.resetPasswordForm.hasError('passwordMismatch') && (this.confirmPasswordControl?.dirty || this.confirmPasswordControl?.touched));
  }

  private buildForm() {
    this.resetPasswordForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(6)]],
    }, { validators: this.passwordMatchValidator });
  }

  private passwordMatchValidator(group: AbstractControl): { [key: string]: boolean } | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    if (password && confirmPassword && password !== confirmPassword) {
      return { passwordMismatch: true };
    }
    return null;
  }

  protected readonly ImageManager = ImageManager;
}
